/**
 * HeroDetailModal.jsx
 * ===================
 * Modular modal for displaying Hero Image details.
 * Shows: Name, Details, History, GPS coords, embedded map, and optional Link URL.
 *
 * Props:
 *   image   – hero image object (see shape below)
 *   onClose – function called when the user dismisses the modal
 *
 * Image shape:
 *   {
 *     id        : number | string,
 *     url       : string,           // image src
 *     name      : string,           // display title
 *     details   : string,           // short description
 *     history   : string,           // historical narrative
 *     address   : string,           // physical address
 *     latitude  : number | string,
 *     longitude : number | string,
 *     link_url  : string,           // optional – clickable "Learn More" URL
 *   }
 */

import React from 'react';
import { X, MapPin, Info, Clock, Navigation, ExternalLink } from 'lucide-react';

export default function HeroDetailModal({ image, onClose }) {
  if (!image) return null;

  const hasGPS    = image.latitude && image.longitude;
  const gmapsUrl  = hasGPS
    ? `https://www.google.com/maps?q=${image.latitude},${image.longitude}`
    : null;
  const embedUrl  = hasGPS
    ? `https://maps.google.com/maps?q=${image.latitude},${image.longitude}&z=15&output=embed`
    : null;
  const linkUrl   = image.link_url || '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Details for ${image.name}`}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header image ──────────────────────────────────────────────── */}
        <div className="relative h-56 overflow-hidden rounded-t-2xl flex-shrink-0">
          <img
            src={image.url}
            alt={image.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 bg-white/20 hover:bg-white/40 text-white rounded-full p-1.5 transition"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Title */}
          <h2 className="absolute bottom-4 left-5 text-white text-2xl font-bold drop-shadow-md pr-12">
            {image.name}
          </h2>
        </div>

        {/* ── Body ──────────────────────────────────────────────────────── */}
        <div className="p-6 space-y-5">

          {/* Optional link URL — shown prominently at the top */}
          {linkUrl && (
            <a
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 w-full px-4 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl transition font-semibold text-sm shadow-sm"
            >
              <ExternalLink className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">Visit Page: {linkUrl}</span>
            </a>
          )}

          {/* Details */}
          {(image.details || image.description) && (
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-800 mb-1">Details</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {image.details || image.description}
                </p>
              </div>
            </div>
          )}

          {/* History */}
          {(image.history || image.story) && (
            <div className="flex gap-3">
              <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-800 mb-1">History</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {image.history || image.story}
                </p>
              </div>
            </div>
          )}

          {/* GPS */}
          {hasGPS && (
            <div className="flex gap-3">
              <Navigation className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-800 mb-1">GPS Coordinates</h3>
                <p className="text-gray-600 text-sm font-mono">
                  {Number(image.latitude).toFixed(6)}°N,&nbsp;
                  {Number(image.longitude).toFixed(6)}°E
                </p>
                {image.address && (
                  <p className="text-gray-500 text-xs mt-1">📍 {image.address}</p>
                )}
                {gmapsUrl && (
                  <a
                    href={gmapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition"
                  >
                    <MapPin className="w-3.5 h-3.5" /> Open in Google Maps
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Embedded map */}
          {embedUrl && (
            <div className="rounded-xl overflow-hidden border border-gray-200">
              <div className="text-sm font-semibold text-gray-700 px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-600" /> Map
              </div>
              <iframe
                title={`map-${image.id}`}
                src={embedUrl}
                width="100%"
                height="220"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
              />
            </div>
          )}
        </div>

        {/* ── Footer ────────────────────────────────────────────────────── */}
        <div className="px-6 pb-5 flex gap-3">
          {linkUrl && (
            <a
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl transition text-sm font-medium"
            >
              <ExternalLink className="w-4 h-4" /> Visit Page
            </a>
          )}
          <button
            onClick={onClose}
            className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
